import { useState } from 'react'
import { UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { useCreateIdmAccMapping, useDeleteIdmAccMapping } from '@/hooks/useIdmAccMappings'
import type { IdmAccMappingRecord } from '@/hooks/useIdmAccMappings'
import { useAuth } from '@/hooks/useAuth'
import { useFilteredQuery } from '@/hooks/useFilteredQuery'
import { FilterBar } from '@/components/FilterBar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

export function IdmAccMappingsPage() {
	const { user } = useAuth()
	const isSuperAdmin = user?.role ? roleAtLeast(user.role, UserRole.SUPER_ADMIN) : false
	const {
		data: mappings,
		isLoading,
		error,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<IdmAccMappingRecord>({
		endpoint: '/employees/idm-acc-mappings',
		queryKey: 'idm-acc-mappings',
	})
	const createMutation = useCreateIdmAccMapping()
	const deleteMutation = useDeleteIdmAccMapping()
	const [isAdding, setIsAdding] = useState(false)
	const [newRow, setNewRow] = useState({
		idmGroup: '',
		accGroup: '',
		tool: '',
		roleInTool: '',
		provisioningMethod: 'manual_ticket',
		isElevatedAccess: false,
		approvalRequired: false,
		approvalOwner: '',
		highRisk: false,
		notes: '',
	})

	const handleSaveNew = async () => {
		if (!newRow.idmGroup || !newRow.accGroup || !newRow.tool) return
		await createMutation.mutateAsync({
			...newRow,
			roleInTool: newRow.roleInTool || null,
			approvalOwner: newRow.approvalOwner || null,
			notes: newRow.notes || null,
		})
		setIsAdding(false)
		setNewRow({
			idmGroup: '',
			accGroup: '',
			tool: '',
			roleInTool: '',
			provisioningMethod: 'manual_ticket',
			isElevatedAccess: false,
			approvalRequired: false,
			approvalOwner: '',
			highRisk: false,
			notes: '',
		})
	}

	const handleDelete = async (id: number) => {
		if (!confirm('Delete this mapping?')) return
		await deleteMutation.mutateAsync(id)
	}

	if (isLoading) return <div className="text-center py-8">Loading mappings...</div>
	if (error) return <div className="text-center py-8 text-red-600">Error: {error.message}</div>

	return (
		<div>
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-gray-900">IDM-to-ACC Mappings</h1>
				{isSuperAdmin && (
					<Button onClick={() => setIsAdding(true)} disabled={isAdding} size="sm">
						Add Mapping
					</Button>
				)}
			</div>

			<FilterBar
				filters={[
					{ key: 'tool', label: 'Tool', type: 'text' },
					{ key: 'idmGroup', label: 'IDM Group', type: 'text' },
					{
						key: 'provisioningMethod',
						label: 'Provisioning',
						type: 'select',
						options: [
							{ label: 'Manual Ticket', value: 'manual_ticket' },
							{ label: 'SSO Automatic', value: 'sso_automatic' },
						],
					},
					{
						key: 'isElevatedAccess',
						label: 'Elevated',
						type: 'select',
						options: [
							{ label: 'Yes', value: 'true' },
							{ label: 'No', value: 'false' },
						],
					},
					{
						key: 'highRisk',
						label: 'High Risk',
						type: 'select',
						options: [
							{ label: 'Yes', value: 'true' },
							{ label: 'No', value: 'false' },
						],
					},
				]}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="border rounded-lg overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>IDM Group</TableHead>
							<TableHead>ACC Group</TableHead>
							<TableHead>Tool</TableHead>
							<TableHead>Role</TableHead>
							<TableHead>Method</TableHead>
							<TableHead>Elevated</TableHead>
							<TableHead>High Risk</TableHead>
							<TableHead>Owner</TableHead>
							{isSuperAdmin && <TableHead className="text-right">Actions</TableHead>}
						</TableRow>
					</TableHeader>
					<TableBody>
						{isAdding && (
							<TableRow>
								<TableCell>
									<Input
										value={newRow.idmGroup}
										onChange={(e) => setNewRow({ ...newRow, idmGroup: e.target.value })}
										placeholder="idm_org_tech"
										className="font-mono text-xs"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.accGroup}
										onChange={(e) => setNewRow({ ...newRow, accGroup: e.target.value })}
										placeholder="acc_github_read"
										className="font-mono text-xs"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.tool}
										onChange={(e) => setNewRow({ ...newRow, tool: e.target.value })}
										placeholder="GitHub"
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.roleInTool}
										onChange={(e) => setNewRow({ ...newRow, roleInTool: e.target.value })}
										placeholder="Read"
									/>
								</TableCell>
								<TableCell>
									<select
										value={newRow.provisioningMethod}
										onChange={(e) => setNewRow({ ...newRow, provisioningMethod: e.target.value })}
										className="border rounded px-2 py-1 text-xs"
									>
										<option value="manual_ticket">Manual Ticket</option>
										<option value="sso_automatic">SSO Automatic</option>
									</select>
								</TableCell>
								<TableCell>
									<input
										type="checkbox"
										checked={newRow.isElevatedAccess}
										onChange={(e) => setNewRow({ ...newRow, isElevatedAccess: e.target.checked })}
									/>
								</TableCell>
								<TableCell>
									<input
										type="checkbox"
										checked={newRow.highRisk}
										onChange={(e) => setNewRow({ ...newRow, highRisk: e.target.checked })}
									/>
								</TableCell>
								<TableCell>
									<Input
										value={newRow.approvalOwner}
										onChange={(e) => setNewRow({ ...newRow, approvalOwner: e.target.value })}
										placeholder="CISO"
									/>
								</TableCell>
								<TableCell className="text-right">
									<div className="flex items-center justify-end gap-1">
										<Button
											onClick={() => void handleSaveNew()}
											size="sm"
											disabled={createMutation.isPending}
											className="h-7 px-2 text-xs"
										>
											Save
										</Button>
										<Button onClick={() => setIsAdding(false)} size="sm" variant="outline" className="h-7 px-2 text-xs">
											Cancel
										</Button>
									</div>
								</TableCell>
							</TableRow>
						)}
						{mappings.map((m) => (
							<TableRow key={m.id}>
								<TableCell className="font-mono text-xs">{m.idmGroup}</TableCell>
								<TableCell className="font-mono text-xs">{m.accGroup}</TableCell>
								<TableCell>{m.tool}</TableCell>
								<TableCell>{m.roleInTool || '-'}</TableCell>
								<TableCell>
									<Badge variant={m.provisioningMethod === 'sso_automatic' ? 'secondary' : 'outline'}>
										{m.provisioningMethod === 'sso_automatic' ? 'SSO' : 'Ticket'}
									</Badge>
								</TableCell>
								<TableCell>{m.isElevatedAccess ? <Badge variant="destructive">Yes</Badge> : '-'}</TableCell>
								<TableCell>{m.highRisk ? <Badge variant="destructive">Yes</Badge> : '-'}</TableCell>
								<TableCell>{m.approvalOwner || '-'}</TableCell>
								{isSuperAdmin && (
									<TableCell className="text-right">
										<Button
											onClick={() => void handleDelete(m.id)}
											size="sm"
											variant="destructive"
											disabled={deleteMutation.isPending}
											className="h-7 px-2 text-xs"
										>
											Delete
										</Button>
									</TableCell>
								)}
							</TableRow>
						))}
						{!isAdding && mappings.length === 0 && (
							<TableRow>
								<TableCell colSpan={isSuperAdmin ? 9 : 8} className="text-center py-8 text-gray-500">
									No mappings found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
